import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import Menu from 'components/Menu/Menu';
import { usePiPWindow } from 'components/PiP/PiPProvider';
import { ZoomControls } from 'features/content-zoom/components/ZoomControls';
import {
  GridPanelFullscreenControl,
  useGridPanelFullscreenControl,
} from 'features/layout/grid-panel/controls/GridPanelFullscreenControl';
import { GridPanelSimulcastLayerControl } from 'features/layout/grid-panel/controls/GridPanelSimulcastLayerControl/GridPanelSimulcastLayerControl';
import {
  ScreensharePanelCloseControl,
  useScreensharePanelCloseControl,
} from 'features/screenshare/components/ScreensharePanelCloseControl';
import { useExternalStreamPanelCloseControl } from 'features/layout/useExternalStreamPanelCloseControl';
import { StreamControlMenuItem } from 'features/streaming/components/StreamControlMenuItem';
import { useBreakpoints } from 'hooks/useBreakpoints';
import React, { useState } from 'react';
import { GridPanelName } from 'features/layout/features/content/types';
import {
  ContentCloseControl,
  useFilePanelCloseControl,
} from 'features/content-library/components/ContentPanel/ContentCloseControl';
import {
  useWhiteboardCloseControl,
  WhiteboardCloseControl,
} from 'features/whiteboard/WhiteboardCloseControl';
import {
  useWhiteboardModeToggleControl,
  WhiteboardModeToggleControl,
} from 'features/whiteboard/WhiteboardModeToggleControl';
import { selectShowGridPanelTabs } from 'features/layout/features/content/contentSlice';
import { selectIsPreviewActive } from 'features/content-library/contentLibrarySlice';
import {
  ContentPreviewModeToggle,
  useContentPreviewModeToggle,
} from 'features/content-library/components/ContentPreviewModeToggle';
import {
  ContentPreviewCloseControl,
  useContentPreviewCloseControl,
} from 'features/layout/features/content/controls/ContentPreviewCloseControl';
import { useAppSelector } from 'store/hooks';
import { useTranslation } from 'react-i18next';
import { GridPanelPushControl } from 'features/layout/grid-panel/controls/GridPanelPushControl/GridPanelPushControl';
import { usePushContentControl } from 'features/layout/grid-panel/controls/GridPanelPushControl/usePushContentControl';
import { GridPanelLockControl } from 'features/layout/grid-panel/controls/GridPanelLockControl/GridPanelLockControl';
import { useLockContentControl } from 'features/layout/grid-panel/controls/GridPanelLockControl/useLockContentControl';
import { GridPanelLockIndicator } from 'features/layout/grid-panel/controls/GridPanelLockIndicator';
import ContentWhiteboardPanelControl from 'features/content-whiteboard/components/ContentWhiteboardPanelControl';
import { useContentBoardPanelControl } from 'features/content-whiteboard/hooks/useContentBoardPanelControl';
import {
  ContentWhiteboardExportControl,
  useContentWhiteboardExportControl,
} from 'features/content-whiteboard/components/ContentWhiteboardExportControl';
import { GridPanelControl } from './GridPanelControl';
import { useGridPanelDragHandle } from './useGridPanelDragHandle';
import { useSimulcastLayerControl } from './controls/GridPanelSimulcastLayerControl/useSimulcastLayerControl';

export const GridPanelControlsContainer = styled('div')(({ theme }) => ({
  position: 'absolute',
  zIndex: 999,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

GridPanelControlsContainer.defaultProps = {
  className: 'grid-panel-controls-container',
};

const closeControlsMap: Record<GridPanelName, React.ComponentType> = {
  whiteboard: () => <WhiteboardCloseControl />,
  contentLibrary: () => <ContentCloseControl />,
  screenshare: () => <ScreensharePanelCloseControl />,
  external: () => null,
};

const SMALL_PANEL_BREAKPOINT = 700;

export type GridPanelControlsProps = {
  activePanel: GridPanelName | null;
  splitBottomControls: boolean;
  panelWidth: number;
};

export const GridPanelControls = ({
  activePanel,
  splitBottomControls,
  panelWidth,
}: GridPanelControlsProps) => {
  const GridPanelCloseControl = activePanel ? closeControlsMap[activePanel] : null;

  const { isMobile } = useBreakpoints();

  const [menuOpen, setMenuOpen] = useState(false);

  const closeMenu = () => setMenuOpen(false);

  const { t } = useTranslation('room');

  const wbConfig = useWhiteboardCloseControl();
  const filePanelConfig = useFilePanelCloseControl();
  const screenshareConfig = useScreensharePanelCloseControl();
  const externalStreamConfig = useExternalStreamPanelCloseControl();

  const {
    controlEnabled: isPushContentEnabled,
    handleClick: handlePushContent,
    label: pushContentLabel,
  } = usePushContentControl();

  const {
    controlEnabled: isLockContentEnabled,
    isLocked: isContentLocked,
    handleClick: handleLockContent,
    label: lockContentLabel,
  } = useLockContentControl();

  const {
    isPrivate,
    controlEnabled: isWhiteboardModeToggleEnabled,
    handleClick: handleWhiteboardModeToggle,
    tooltip: whiteboardModeToggleLabel,
  } = useWhiteboardModeToggleControl();

  const { controlEnabled: simulcastControlEnabled } = useSimulcastLayerControl();

  const { pipWindow } = usePiPWindow();

  const hasDragHandle = useGridPanelDragHandle();

  const showTabs = useAppSelector(selectShowGridPanelTabs);
  const isPreviewActive = useAppSelector(selectIsPreviewActive);

  const {
    controlEnabled: fullscreenEnabled,
    icon,
    tooltip,
    handleClick: toggleFullscreen,
  } = useGridPanelFullscreenControl();

  const { controlEnabled: contentBoardPanelEnabled } = useContentBoardPanelControl();
  const {
    controlEnabled: exportContentWhiteboardEnabled,
    handleClick: handleExportContentWhiteboard,
  } = useContentWhiteboardExportControl();

  const {
    controlEnabled: previewModeToggleEnabled,
    handleClick: handlePreviewModeToggle,
    label: previewModeLabel,
  } = useContentPreviewModeToggle();
  const { handleClick: handleClosePreview, label: closePreviewLabel } =
    useContentPreviewCloseControl();

  const isSmallPanel = panelWidth < SMALL_PANEL_BREAKPOINT;

  if (isPreviewActive) {
    const showPreviewMobileMenu = isMobile || isSmallPanel;

    return (
      <GridPanelControlsContainer
        className={!isMobile && !pipWindow ? 'hover-child' : ''}
        sx={{
          height: isMobile ? '32px' : 'auto',
          top: 10,
          right: hasDragHandle ? '24px' : '12px',
        }}
      >
        {showPreviewMobileMenu ? (
          <Box
            sx={{
              marginRight: '-6px',
              zIndex: 3,
              textAlign: 'left',
            }}
          >
            <Menu
              id="grid-panel-preview-controls"
              open={menuOpen}
              onClose={closeMenu}
              placement="bottom-end"
              trigger={
                <GridPanelControl icon="settings1" onClick={() => setMenuOpen((open) => !open)} />
              }
              PaperProps={{
                sx: (theme) => ({
                  backgroundColor: theme.palette.grey['900'],
                }),
              }}
              PopperProps={{ disablePortal: !isMobile }}
              sx={(theme) => ({
                color: theme.palette.common.white,
              })}
            >
              {previewModeToggleEnabled && (
                <StreamControlMenuItem
                  icon="eye"
                  text={previewModeLabel}
                  onClick={handlePreviewModeToggle}
                />
              )}
              {fullscreenEnabled && !isMobile && (
                <StreamControlMenuItem icon={icon} text={tooltip} onClick={toggleFullscreen} />
              )}
              <StreamControlMenuItem
                icon="close"
                text={closePreviewLabel}
                onClick={handleClosePreview}
              />
            </Menu>
          </Box>
        ) : (
          <>
            <ContentPreviewModeToggle />
            {fullscreenEnabled && <GridPanelFullscreenControl />}
            <ContentPreviewCloseControl />
          </>
        )}
      </GridPanelControlsContainer>
    );
  }

  if (!activePanel) {
    return null;
  }

  const config: Record<GridPanelName, { controlEnabled: boolean; handleClick: () => void }> = {
    whiteboard: wbConfig,
    contentLibrary: filePanelConfig,
    screenshare: screenshareConfig,
    external: externalStreamConfig,
  };

  const { controlEnabled, handleClick } = config[activePanel];

  const pushContentEnabled = activePanel !== 'external' && isPushContentEnabled;
  const lockContentEnabled = activePanel !== 'external' && isLockContentEnabled;

  const availableMobileControlsCount = [
    pushContentEnabled,
    lockContentEnabled,
    controlEnabled,
    isWhiteboardModeToggleEnabled,
    exportContentWhiteboardEnabled,
  ].filter(Boolean).length;

  const showMobileMenu = (isMobile || isSmallPanel) && availableMobileControlsCount > 1;

  return (
    <>
      <GridPanelControlsContainer
        className={!isMobile && !pipWindow ? 'hover-child' : ''}
        sx={{
          height: isMobile ? '32px' : 'auto',
          top: showTabs ? 50 : 10,
          right: hasDragHandle ? '24px' : '12px',
          marginTop: isMobile ? 0 : 0,
        }}
      >
        {showMobileMenu ? (
          <Box
            sx={{
              marginRight: '-6px',
              zIndex: 3,
              textAlign: 'left',
            }}
          >
            {controlEnabled ||
            pushContentEnabled ||
            lockContentEnabled ||
            isWhiteboardModeToggleEnabled ? (
              <Menu
                id="grid-panel-controls"
                open={menuOpen}
                onClose={closeMenu}
                placement="bottom-end"
                trigger={
                  <GridPanelControl icon="settings1" onClick={() => setMenuOpen((open) => !open)} />
                }
                PaperProps={{
                  sx: (theme) => ({
                    backgroundColor: theme.palette.grey['900'],
                  }),
                }}
                PopperProps={{ disablePortal: !isMobile }}
                sx={(theme) => ({
                  color: theme.palette.common.white,
                })}
              >
                {isWhiteboardModeToggleEnabled && (
                  <StreamControlMenuItem
                    icon={isPrivate ? 'eye-crossed' : 'eye'}
                    text={whiteboardModeToggleLabel}
                    onClick={handleWhiteboardModeToggle}
                  />
                )}
                {pushContentEnabled && (
                  <StreamControlMenuItem
                    icon="box-arrow"
                    text={pushContentLabel}
                    onClick={handlePushContent}
                  />
                )}
                {lockContentEnabled && (
                  <StreamControlMenuItem
                    icon={isContentLocked ? 'unlock' : 'lock'}
                    text={lockContentLabel}
                    onClick={handleLockContent}
                  />
                )}
                {exportContentWhiteboardEnabled && (
                  <StreamControlMenuItem
                    icon="download"
                    text={t('grid.export_draw')}
                    onClick={handleExportContentWhiteboard}
                  />
                )}
                {fullscreenEnabled && !isMobile && (
                  <StreamControlMenuItem icon={icon} text={tooltip} onClick={toggleFullscreen} />
                )}
                {controlEnabled && (
                  <StreamControlMenuItem
                    icon="close"
                    text={t('grid.close_panel')}
                    onClick={handleClick}
                  />
                )}
              </Menu>
            ) : null}
          </Box>
        ) : (
          <>
            <GridPanelLockIndicator />
            {isWhiteboardModeToggleEnabled && <WhiteboardModeToggleControl />}
            {lockContentEnabled && <GridPanelLockControl />}
            {pushContentEnabled && <GridPanelPushControl />}
            {exportContentWhiteboardEnabled && <ContentWhiteboardExportControl />}
            {simulcastControlEnabled && (
              <GridPanelSimulcastLayerControl compact={isMobile || isSmallPanel} />
            )}
            {fullscreenEnabled && <GridPanelFullscreenControl />}
            {controlEnabled && GridPanelCloseControl && <GridPanelCloseControl />}
          </>
        )}
      </GridPanelControlsContainer>
      <GridPanelControlsContainer
        sx={{
          bottom: '10px',
          zIndex: 1000,
          userSelect: 'none',
          pointerEvents: 'none',
          WebkitTapHighlightColor: 'transparent',
          ...(splitBottomControls
            ? {
                left: { xs: '10px', md: '12px' },
                right: { xs: '10px', md: '24px' },
                justifyContent: 'space-between',
              }
            : {
                right: { xs: '20px', md: '24px' },
              }),
        }}
      >
        <ZoomControls />
        {contentBoardPanelEnabled && <ContentWhiteboardPanelControl />}
      </GridPanelControlsContainer>
    </>
  );
};
